<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-secondary"
          :disabled="printing || selectedIds.length === 0"
          @click="printSelected"
        >
          {{ printing ? '列印中' : '列印託運單' }}
        </button>
        <button
          class="btn btn-primary"
          :disabled="isWixtarClient"
          :title="isWixtarClient ? 'Wixtar 會員系統：請至 Wixtar 建立訂單' : ''"
          @click="handleCreate"
        >
                <i class="ri-add-line"></i>
                新增訂單
              </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>訂單以 Wixtar 為準，本頁列表為 CRM 鏡像（Webhook 同步）。
      新增訂單請至 Wixtar POS 操作；於 CRM 自行建單會與 Wixtar 回推的訂單重複。
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋訂單編號、會員姓名、電話..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="pending_fulfillment">待履約</option>
          <option value="preparing">備貨中</option>
          <option value="shipped">已出貨</option>
          <option value="arrived_store">已到店</option>
          <option value="picked_up">客戶已取貨</option>
          <option value="delivered">宅配已送達</option>
          <option value="returned_unclaimed">到店未取退回</option>
          <option value="returned_customer">客戶退貨</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterPaymentStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部付款狀態</option>
          <option value="pending">待付款</option>
          <option value="paid">已付款</option>
          <option value="failed">付款失敗</option>
          <option value="refunded">已退款</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterOrderSource" class="form-control" @change="handleFilterChange">
          <option value="">全部來源</option>
          <option value="pos">POS</option>
          <option value="shopify">Shopify</option>
          <option value="91app">91APP</option>
          <option value="shopline">Shopline</option>
          <option value="batch_import">批次匯入</option>
          <option value="lockline_commerce">商城</option>
          <option value="manual">手動建立</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchOrders">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th></th>
            <th>訂單編號</th>
            <th>會員</th>
            <th>訂單日期</th>
            <th>訂單來源</th>
            <th>訂單狀態</th>
            <th>撤銷狀態</th>
            <th>付款狀態</th>
            <th>訂單金額</th>
            <th>獲得點數</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!orders || orders.length === 0">
            <td colspan="11" class="empty-state">
              沒有找到訂單
            </td>
          </tr>
          <tr v-for="order in orders" :key="order.id">
            <td>
              <input
                v-if="canPrintLogistics(order)"
                type="checkbox"
                :checked="selectedIds.includes(order.id)"
                @change="toggleSelected(order.id)"
              />
            </td>
            <td>{{ order.orderNumber || '-' }}</td>
            <td>
              <span v-if="order.memberName">{{ order.memberName }}</span>
              <span v-else class="text-muted">非會員</span>
            </td>
            <td>{{ formatDate(order.orderDate) }}</td>
            <td>
              <span class="badge badge-info">{{ getOrderSourceText(order.orderSource) }}</span>
            </td>
            <td>
              <span :class="getStatusBadgeClass(getDisplayOrderStatus(order))">
                {{ getStatusText(getDisplayOrderStatus(order)) }}
              </span>
            </td>
            <td>
              <span
                v-if="order.importRevokeStatus"
                :class="getImportRevokeBadgeClass(order.importRevokeStatus)"
              >
                {{ getImportRevokeStatusText(order.importRevokeStatus) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span :class="getPaymentStatusBadgeClass(order.paymentStatus)">
                {{ getPaymentStatusText(order.paymentStatus) }}
              </span>
            </td>
            <td>{{ formatCurrency(order.totalAmount) }}</td>
            <td>{{ formatNumber(order.pointsEarned || 0) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleView(order)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { orderService } from '@/api/modules/orders.js';
import { orderSourceLabel } from '@/utils/orderLabels.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'ClientOrders',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isUnboundPage = computed(() => route.name === 'ClientOrdersUnbound');
    const { pageTitle } = usePageTitle(isUnboundPage.value ? '未綁定訂單' : '訂單列表');
    const orders = ref([]);
    const selectedIds = ref([]);
    const printing = ref(false);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const filterPaymentStatus = ref('');
    const filterOrderSource = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);
    const isWixtarClient = ref(false);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const canPrintLogistics = (order) => order?.shippingMethod === 'ecpay'
      && String(order?.logisticsId || '').trim() !== '';

    const toggleSelected = (orderId) => {
      selectedIds.value = selectedIds.value.includes(orderId)
        ? selectedIds.value.filter((id) => id !== orderId)
        : [...selectedIds.value, orderId];
    };

    const printSelected = async () => {
      const chosen = orders.value.filter((order) => selectedIds.value.includes(order.id));
      const subtypes = new Set(chosen.map((order) => String(order.logisticsSubtype || '').trim()));
      if (subtypes.size !== 1) {
        await showError('無法列印託運單', '請選擇相同的寄送方式再列印');
        return;
      }
      const popup = window.open('', '_blank');
      if (!popup) {
        await showError('無法開啟託運單', '請允許彈出視窗後再列印');
        return;
      }
      printing.value = true;
      try {
        const response = await orderService.printLogisticsDocuments(chosen.map((order) => order.id));
        const html = response?.data?.html;
        if (!response?.success || !html) {
          popup.close();
          await showError('無法列印託運單', response?.message || '綠界沒有回傳託運單');
          return;
        }
        popup.document.open();
        popup.document.write(html);
        popup.document.close();
        selectedIds.value = [];
      } catch (err) {
        popup.close();
        await showError('無法列印託運單', err.message || '綠界沒有回傳託運單');
      } finally {
        printing.value = false;
      }
    };

    const fetchOrders = async () => {
      loading.value = true;
      error.value = null;
      selectedIds.value = [];

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterStatus.value) {
          if (filterStatus.value === 'cancelled') {
            params.orderStatus = 'cancelled';
          } else {
            params.logisticsStatus = filterStatus.value;
          }
        }

        if (filterPaymentStatus.value) {
          params.paymentStatus = filterPaymentStatus.value;
        }

        if (filterOrderSource.value) {
          params.orderSource = filterOrderSource.value;
        }

        // 根據路由決定調用哪個 API
        const response = isUnboundPage.value 
          ? await orderService.getUnboundOrders(params)
          : await orderService.getOrders(params);

        if (response && response.success && response.data) {
          orders.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;
        } else {
          const errorMsg = (response && response.message) || '獲取訂單列表失敗';
          error.value = errorMsg;
        }
      } catch (err) {
        console.error('獲取訂單列表失敗:', err);
        error.value = err.message || err.data?.message || '獲取訂單列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchOrders();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchOrders();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchOrders();
      }
    };

    const handleView = (order) => {
      router.push(`/client/orders/${order.id}`);
    };

    const handleCreate = () => {
      if (isWixtarClient.value) return;
      router.push('/client/orders/create');
    };

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
      } catch (err) {
        console.error('載入客戶會員系統類型失敗:', err);
        isWixtarClient.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return 'NT$ 0';
      return `NT$ ${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatNumber = (number) => {
      if (number === null || number === undefined) return '0';
      return Number(number).toLocaleString('zh-TW');
    };

    const getDisplayOrderStatus = (order) => {
      if (order.orderStatus === 'cancelled') {
        return 'cancelled';
      }
      return order.logisticsStatus || order.status;
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        pending: 'badge badge-warning',
        pending_fulfillment: 'badge badge-warning',
        preparing: 'badge badge-info',
        shipped: 'badge badge-primary',
        arrived_store: 'badge badge-info',
        picked_up: 'badge badge-success',
        delivered: 'badge badge-success',
        returned_unclaimed: 'badge badge-danger',
        returned_customer: 'badge badge-danger',
        cancelled: 'badge badge-danger',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        pending_fulfillment: '待履約',
        preparing: '備貨中',
        shipped: '已出貨',
        arrived_store: '已到店',
        picked_up: '客戶已取貨',
        delivered: '宅配已送達',
        returned_unclaimed: '到店未取退回',
        returned_customer: '客戶退貨',
        cancelled: '已取消',
      };
      return statusMap[status] || status;
    };

    const getImportRevokeBadgeClass = (status) => {
      const statusMap = {
        active: 'badge badge-success',
        revoked: 'badge badge-danger',
        failed: 'badge badge-warning',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getImportRevokeStatusText = (status) => {
      const statusMap = {
        active: '有效',
        revoked: '已撤銷',
        failed: '撤銷失敗',
      };
      return statusMap[status] || status;
    };

    const getPaymentStatusBadgeClass = (status) => {
      const statusMap = {
        pending: 'badge badge-warning',
        paid: 'badge badge-success',
        failed: 'badge badge-secondary',
        refunded: 'badge badge-danger',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getPaymentStatusText = (status) => {
      const statusMap = {
        pending: '待付款',
        paid: '已付款',
        failed: '付款失敗',
        refunded: '已退款',
      };
      return statusMap[status] || status;
    };

    const getOrderSourceText = orderSourceLabel;

    onMounted(() => {
      fetchOrders();
      loadClientMemberSystem();
    });

    return {
      orders,
      selectedIds,
      printing,
      loading,
      error,
      isWixtarClient,
      searchQuery,
      filterStatus,
      filterPaymentStatus,
      filterOrderSource,
      currentPage,
      totalPages,
      total,
      pageTitle,
      isUnboundPage,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleView,
      handleCreate,
      canPrintLogistics,
      toggleSelected,
      printSelected,
      formatDate,
      formatCurrency,
      formatNumber,
      getDisplayOrderStatus,
      getStatusBadgeClass,
      getStatusText,
      getImportRevokeBadgeClass,
      getImportRevokeStatusText,
      getPaymentStatusBadgeClass,
      getPaymentStatusText,
      getOrderSourceText,
      fetchOrders,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.wixtar-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #f0f7ff;
  color: #1e293b;
  font-size: 14px;
  line-height: 1.7;
}

.table-wrapper table {
  min-width: 1320px;
}
</style>
